import React, { useState } from 'react';
import { TicketIcon, MessageCircleIcon, PhoneIcon, MailIcon, ClockIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon } from 'lucide-react';
type Ticket = {
  id: string;
  subject: string;
  description: string;
  status: 'open' | 'closed';
  date: string;
  lastUpdate: string;
};
export const HelpSupport = () => {
  const [activeTab, setActiveTab] = useState('submit');
  const [tickets] = useState<Ticket[]>([{
    id: 'TK-001',
    subject: 'API Integration Issue',
    description: 'Having trouble connecting to the API endpoint...',
    status: 'open',
    date: '2023-06-15',
    lastUpdate: '2023-06-16'
  }, {
    id: 'TK-002',
    subject: 'Billing Question',
    description: "Need clarification about last month's charges...",
    status: 'closed',
    date: '2023-06-10',
    lastUpdate: '2023-06-12'
  }]);
  return <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8" data-id="element-1307">
      <div className="max-w-4xl mx-auto" data-id="element-1308">
        <h1 className="text-2xl font-bold mb-8" data-id="element-1309">Help & Support</h1>
        {/* Tab Navigation */}
        <div className="flex space-x-4 mb-8" data-id="element-1310">
          <button onClick={() => setActiveTab('submit')} className={`px-4 py-2 rounded-lg ${activeTab === 'submit' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-1311">
            Submit Ticket
          </button>
          <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-lg ${activeTab === 'history' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-1312">
            Ticket History
          </button>
          <button onClick={() => setActiveTab('contact')} className={`px-4 py-2 rounded-lg ${activeTab === 'contact' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} data-id="element-1313">
            Contact
          </button>
        </div>
        {/* Submit Ticket Form */}
        {activeTab === 'submit' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1314">
            <h2 className="text-xl font-semibold mb-6" data-id="element-1315">
              Submit a Support Ticket
            </h2>
            <form className="space-y-6" data-id="element-1316">
              <div data-id="element-1317">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1318">
                  Subject
                </label>
                <input type="text" className="w-full border-gray-200 rounded-lg" placeholder="Brief description of your issue" data-id="element-1319" />
              </div>
              <div data-id="element-1320">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1321">
                  Category
                </label>
                <select className="w-full border-gray-200 rounded-lg" data-id="element-1322">
                  <option data-id="element-1323">Technical Support</option>
                  <option data-id="element-1324">Billing</option>
                  <option data-id="element-1325">Account Issues</option>
                  <option data-id="element-1326">Feature Request</option>
                </select>
              </div>
              <div data-id="element-1327">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1328">
                  Description
                </label>
                <textarea className="w-full border-gray-200 rounded-lg" rows={6} placeholder="Please provide as much detail as possible..." data-id="element-1329" />
              </div>
              <button type="submit" className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" data-id="element-1330">
                Submit Ticket
              </button>
            </form>
          </div>}
        {/* Ticket History */}
        {activeTab === 'history' && <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1331">
            <div className="divide-y divide-gray-200" data-id="element-1332">
              {tickets.map(ticket => <div key={ticket.id} className="p-6" data-id="element-1333">
                  <div className="flex items-center justify-between mb-4" data-id="element-1334">
                    <div className="flex items-center space-x-4" data-id="element-1335">
                      <span className="text-sm font-medium text-gray-500" data-id="element-1336">
                        {ticket.id}
                      </span>
                      <h3 className="font-medium" data-id="element-1337">{ticket.subject}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${ticket.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1338">
                        {ticket.status}
                      </span>
                    </div>
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" data-id="element-1339" />
                  </div>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-1340">
                    {ticket.description}
                  </p>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-1341">
                    <ClockIcon className="h-4 w-4 mr-1" data-id="element-1342" />
                    <span data-id="element-1343">Created: {ticket.date}</span>
                    <span className="mx-2" data-id="element-1344">•</span>
                    <span data-id="element-1345">Last Update: {ticket.lastUpdate}</span>
                  </div>
                </div>)}
            </div>
          </div>}
        {/* Contact Information */}
        {activeTab === 'contact' && <div className="space-y-6" data-id="element-1346">
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1347">
              <h2 className="text-xl font-semibold mb-6" data-id="element-1348">
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1349">
                <div className="flex items-center space-x-4" data-id="element-1350">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-1351">
                    <MessageCircleIcon className="h-5 w-5 text-indigo-600" data-id="element-1352" />
                  </div>
                  <div data-id="element-1353">
                    <h3 className="font-medium" data-id="element-1354">Live Chat</h3>
                    <p className="text-sm text-gray-500" data-id="element-1355">Available 24/7</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-1356">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-1357">
                    <PhoneIcon className="h-5 w-5 text-indigo-600" data-id="element-1358" />
                  </div>
                  <div data-id="element-1359">
                    <h3 className="font-medium" data-id="element-1360">Phone Support</h3>
                    <p className="text-sm text-gray-500" data-id="element-1361">+1 (555) 123-4567</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4" data-id="element-1362">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center" data-id="element-1363">
                    <MailIcon className="h-5 w-5 text-indigo-600" data-id="element-1364" />
                  </div>
                  <div data-id="element-1365">
                    <h3 className="font-medium" data-id="element-1366">Email</h3>
                    <p className="text-sm text-gray-500" data-id="element-1367">
                      support@realuseai.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-6" data-id="element-1368">
              <h3 className="font-medium text-indigo-900 mb-2" data-id="element-1369">
                Business Hours
              </h3>
              <p className="text-sm text-indigo-700" data-id="element-1370">
                Monday - Friday: 9:00 AM - 6:00 PM EST
                <br data-id="element-1371" />
                Saturday: 10:00 AM - 4:00 PM EST
                <br data-id="element-1372" />
                Sunday: Closed
              </p>
            </div>
          </div>}
      </div>
    </div>;
};